import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

export default function Dashboard({ data }) {
  if (!data) return null;

  const users = Array.isArray(data.users) ? data.users : [];
  const employees = Array.isArray(data.employees) ? data.employees : [];
  const logs = Array.isArray(data.logs) ? data.logs : [];
  const snapshots = Array.isArray(data.snapshots) ? data.snapshots : [];

  /* ========= OVERALL COUNTS ========= */
  const overallPie = {
    labels: ["Users", "Employees", "Logs", "Snapshots"],
    datasets: [
      {
        data: [
          users.length,
          employees.length,
          logs.length,
          snapshots.length,
        ],
        backgroundColor: [
          "#0ea5e9",
          "#22c55e",
          "#f59e0b",
          "#8b5cf6",
        ],
        borderWidth: 0,
      },
    ],
  };

  /* ========= USERS CREATED (PIE) ========= */
  const usersByDate = users.reduce((acc, u) => {
    if (!u.created_at) return acc;
    const d = new Date(u.created_at).toISOString().split("T")[0];
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});

  const usersCreatedPie = {
    labels: Object.keys(usersByDate),
    datasets: [
      {
        data: Object.values(usersByDate),
        backgroundColor: Object.keys(usersByDate).map(
          (_, i) => `hsl(${i * 55},70%,60%)`
        ),
        borderWidth: 0,
      },
    ],
  };

  /* ========= EMPLOYEES CREATED (LINE) ========= */
  const employeesByDate = employees.reduce((acc, e) => {
    if (!e.created_at) return acc;
    const d = new Date(e.created_at).toISOString().split("T")[0];
    acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});

  const empDates = Object.keys(employeesByDate).sort();
  const empCounts = empDates.map(d => employeesByDate[d]);

  const employeesLine = {
    labels: empDates,
    datasets: [
      {
        data: empCounts,
        borderColor: "#22c55e",
        backgroundColor: "rgba(34,197,94,0.18)",
        fill: true,
        tension: 0.35,
        pointRadius: 3,
      },
    ],
  };

  /* ========= EMPLOYEE STATUS ========= */
  const now = Date.now();
  const THRESHOLD = 5;

  let online = 0;
  let offline = 0;

  employees.forEach(e => {
    const empLogs = logs.filter(
      l => l.employee_id === e.id && l.created_at
    );

    if (!empLogs.length) {
      offline++;
      return;
    }

    const last = Math.max(
      ...empLogs.map(l => new Date(l.created_at).getTime())
    );

    (now - last) / 60000 <= THRESHOLD ? online++ : offline++;
  });

  const employeeStatusPie = {
    labels: ["Online", "Offline"],
    datasets: [
      {
        data: [online, offline],
        backgroundColor: ["#22c55e", "#ef4444"],
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="dashboard-grid compact">
      {/* ===== Row 1 ===== */}
      <div className="dashboard-card small">
        <h4>System Overview</h4>
        <Pie data={overallPie} />
      </div>

      <div className="dashboard-card small">
        <h4>Users Created</h4>
        {usersCreatedPie.labels.length ? (
          <Pie data={usersCreatedPie} />
        ) : (
          <span className="muted">No data</span>
        )}
      </div>

      {/* ===== Row 2 ===== */}
      <div className="dashboard-card wide">
        <h4>Employees Growth</h4>
        {empDates.length ? (
          <Line data={employeesLine} />
        ) : (
          <span className="muted">No data</span>
        )}
      </div>

      <div className="dashboard-card small">
        <h4>Employee Status</h4>
        <Pie data={employeeStatusPie} />
        <span className="hint">Last activity ≤ 5 min</span>
      </div>
    </div>
  );
}
